import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, MenuItem, TextField } from "@mui/material";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  applyAutoRolesNow,
  createCustomRole,
  previewAutoRoles,
  saveAutoRoles,
} from "./accessApi";
import { Notice, RoleChip } from "./accessUi";

const FIELDS = [
  { value: "title", label: "Job title" },
  { value: "department", label: "Department" },
  { value: "branch", label: "Branch" },
  { value: "emailDomain", label: "Email domain" },
  { value: "salesRep", label: "Has a sales rep code" },
  { value: "anyone", label: "Anyone left over" },
];
const NO_VALUE = ["salesRep", "anyone"];
const PREVIEW_SHOWN = 100;

const newRule = () => ({
  id: `rule-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
  field: "title",
  op: "contains",
  value: "",
  roles: [],
});

const ruleReady = (rule) => rule.roles.length > 0 && (NO_VALUE.includes(rule.field) || rule.value.trim());

// Automatic roles for people with none: rules, a preview, and "Apply now".
const AutoRolesCard = ({ autoRoles, roleCatalog, branchOptions, actorId, onChanged }) => {
  const [enabled, setEnabled] = useState(autoRoles.enabled);
  const [rules, setRules] = useState(autoRoles.rules);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setEnabled(autoRoles.enabled);
    setRules(autoRoles.rules);
  }, [autoRoles]);

  const dirty =
    enabled !== autoRoles.enabled || JSON.stringify(rules) !== JSON.stringify(autoRoles.rules);
  const roleNames = useMemo(() => roleCatalog.map((r) => r.name), [roleCatalog]);
  const readyRules = rules.filter(ruleReady);

  const update = (id, patch) => {
    setPreview(null);
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const run = async (fn) => {
    setBusy(true);
    setMessage(null);
    try {
      const text = await fn();
      if (text) setMessage({ type: "success", text });
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Couldn't do that." });
    } finally {
      setBusy(false);
    }
  };

  const showPreview = () =>
    run(async () => {
      const { matches } = await previewAutoRoles(actorId, readyRules);
      setPreview(matches);
      return null;
    });

  const save = () =>
    run(async () => {
      // Role names typed in that don't exist yet become custom roles.
      const known = new Set(roleNames.map((n) => n.toLowerCase()));
      const missing = [...new Set(readyRules.flatMap((r) => r.roles))].filter((n) => !known.has(n.toLowerCase()));
      for (const name of missing) await createCustomRole(actorId, name, "Created for an automatic role rule");
      await saveAutoRoles(actorId, { enabled, rules: readyRules });
      await onChanged();
      return `Saved${missing.length ? ` and created ${missing.join(", ")}` : ""}. ${
        enabled ? "New people with no roles get these at their next sign-in." : "Rules are off."
      }`;
    });

  const applyNow = () =>
    run(async () => {
      const { changed } = await applyAutoRolesNow(actorId);
      setPreview(null);
      await onChanged();
      return changed.length
        ? `Gave roles to ${changed.length} ${changed.length === 1 ? "person" : "people"}. Each got a notification.`
        : "Nobody without roles matched the rules.";
    });

  return (
    <section className="app-card rounded-2xl border p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="app-icon-tile h-10 w-10 shrink-0 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
            <AutoFixHighRoundedIcon fontSize="small" />
          </span>
          <div className="min-w-0">
            <h2 className="font-semibold">Give roles to people who have none</h2>
            <p className="app-muted text-sm">
              Runs when someone signs in and after every directory sync. Anyone who already has a role is left alone.
            </p>
          </div>
        </div>
        <label className="app-chip cursor-pointer select-none py-1.5">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={busy}
            className="mr-1 accent-sky-600"
          />
          On
        </label>
      </div>

      <Notice message={message} className="mt-4" />

      <div className="mt-4 space-y-2">
        {rules.length === 0 && (
          <p className="app-muted text-sm">
            No rules yet. Add one like “Job title contains Driver → Driver”, or “Anyone left over → Office” as a default.
          </p>
        )}
        {rules.map((rule, i) => (
          <div
            key={rule.id}
            className="app-surface grid gap-2 rounded-xl border border-[color:var(--app-surface-border)] p-3 md:grid-cols-[auto_minmax(0,11rem)_minmax(0,7rem)_minmax(0,1fr)_minmax(0,1.3fr)_auto] md:items-center"
          >
            <span className="app-muted text-xs font-semibold md:w-6">{i === 0 ? "If" : "Or"}</span>
            <TextField
              select
              size="small"
              value={rule.field}
              onChange={(e) => update(rule.id, { field: e.target.value, value: "" })}
              inputProps={{ "aria-label": "Field" }}
            >
              {FIELDS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>
            {NO_VALUE.includes(rule.field) ? (
              <span className="app-muted text-xs md:col-span-2">
                {rule.field === "anyone" ? "Everyone no other rule matched" : "Their directory record has a sales rep code"}
              </span>
            ) : (
              <>
                <TextField
                  select
                  size="small"
                  value={rule.op}
                  onChange={(e) => update(rule.id, { op: e.target.value })}
                  inputProps={{ "aria-label": "Match" }}
                >
                  <MenuItem value="contains">contains</MenuItem>
                  <MenuItem value="is">is</MenuItem>
                </TextField>
                {rule.field === "branch" ? (
                  <TextField
                    select
                    size="small"
                    value={rule.value}
                    onChange={(e) => update(rule.id, { value: e.target.value, op: "is" })}
                    inputProps={{ "aria-label": "Branch" }}
                  >
                    {branchOptions.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    size="small"
                    value={rule.value}
                    placeholder={rule.field === "emailDomain" ? "priebeswoodworking.com" : "Driver"}
                    onChange={(e) => update(rule.id, { value: e.target.value })}
                    inputProps={{ "aria-label": "Value", maxLength: 120 }}
                  />
                )}
              </>
            )}
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={roleNames}
              value={rule.roles}
              onChange={(e, value) =>
                update(rule.id, { roles: [...new Set(value.map((v) => String(v).trim()).filter(Boolean))] })
              }
              renderTags={(value, getTagProps) =>
                value.map((role, index) => {
                  const { key, onDelete } = getTagProps({ index });
                  return (
                    <span key={key} className="m-0.5">
                      <RoleChip role={role} onRemove={onDelete} />
                    </span>
                  );
                })
              }
              renderInput={(params) => <TextField {...params} placeholder={rule.roles.length ? "" : "→ give role(s)…"} />}
            />
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setRules((prev) => prev.filter((r) => r.id !== rule.id));
              }}
              aria-label="Delete rule"
              className="app-icon-btn justify-self-end"
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setRules((prev) => [...prev, newRule()])}
          disabled={busy}
          className="app-btn app-btn-sm app-btn-secondary"
        >
          <AddRoundedIcon sx={{ fontSize: 16 }} />
          Add rule
        </button>
        <button
          type="button"
          onClick={showPreview}
          disabled={busy || !readyRules.length}
          className="app-btn app-btn-sm app-btn-secondary"
        >
          Preview
        </button>
        <span className="flex-1" />
        {dirty && (
          <button type="button" onClick={save} disabled={busy} className="app-btn app-btn-sm app-btn-primary">
            Save rules
          </button>
        )}
        {!dirty && autoRoles.enabled && autoRoles.rules.length > 0 && (
          <button type="button" onClick={applyNow} disabled={busy} className="app-btn app-btn-sm app-btn-primary">
            Apply now to everyone with no roles
          </button>
        )}
      </div>
      <p className="app-muted mt-2 text-xs">
        Type a role name that doesn't exist yet to create it as a custom role when you save. Department is
        often blank in Microsoft 365, so job title usually works better.
      </p>

      {preview && (
        <div className="mt-4">
          <p className="app-eyebrow mb-2">
            {preview.length
              ? `${preview.length} ${preview.length === 1 ? "person" : "people"} with no roles would get…`
              : "Nobody with no roles matches these rules."}
          </p>
          {preview.length > 0 && (
            <ul className="app-surface max-h-80 divide-y divide-[color:var(--app-divider)] overflow-y-auto rounded-xl border border-[color:var(--app-surface-border)]">
              {preview.slice(0, PREVIEW_SHOWN).map((m) => (
                <li key={m._id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{m.name}</p>
                    <p className="app-muted truncate text-xs">{[m.title, m.branch].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {m.roles.map((r) => (
                      <RoleChip key={r} role={r} />
                    ))}
                  </div>
                </li>
              ))}
              {preview.length > PREVIEW_SHOWN && (
                <li className="app-muted px-3 py-2 text-xs">and {preview.length - PREVIEW_SHOWN} more</li>
              )}
            </ul>
          )}
          {dirty && preview.length > 0 && (
            <p className="app-muted mt-2 text-xs">Save the rules, then “Apply now” to give these out.</p>
          )}
        </div>
      )}
    </section>
  );
};

export default AutoRolesCard;
